// src/components/lead/sections/help/AdsAssistantHelp.tsx
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { canEditAdsGuidance } from "@/lib/adsAssistantPermissions";
import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { shortOrgId } from "@/components/ads-assistant/adsAssistantPanelUtils";

export function AdsAssistantHelp() {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const { brandName, appDisplayName } = useOrgSettings();
  const canAccessLibrary = canEditAdsGuidance(auth);
  const primaryOrgId = auth.activeOrganizationId;
  const workspaceOverrideActive = !!scopedOrgId && !!primaryOrgId && scopedOrgId !== primaryOrgId;

  return (
    <div className="text-sm text-blue-900 space-y-3 text-left">
      <p>
        Three tabs: <strong>Library</strong> (categories, templates, policies, social links, optional AI drafting
        help), <strong>Ingestion</strong> (log posts, feed import, index), and <strong>Suggestion</strong> (browse
        coaching hints). Data follows the scoped organization id (same as KB / CRM when you use a workspace
        override).
      </p>
      <p>
        Org-scoped <strong>notes</strong> and <strong>bookmarks</strong> live under support tools at the bottom of
        the mode.
      </p>

      <div className="rounded-xl border border-gray-200/90 bg-white/70 px-3 py-3 text-sm text-gray-800 shadow-sm dark:border-gray-600 dark:bg-zinc-900/70 dark:text-gray-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          How the tabs work
        </p>
        <p className="mt-2 leading-relaxed text-gray-800 dark:text-gray-100">
          {canAccessLibrary ? (
            <>
              <strong>Library</strong> — categories, templates, policy hints, and social mappings.{" "}
              <strong>Ingestion</strong> — paste ad copy and save to the index (no extra wrapper).{" "}
              <strong>Suggestion</strong> — browse indexed posts, stats, and coaching hints.{" "}
              <strong>Feed import</strong> (trainer+) — Meta / Google fetch or paste JSON into the same index.{" "}
              <strong>Org Settings</strong> (trainer+) — platforms, retention, activity toggles, suggestion defaults.
            </>
          ) : (
            <>
              <strong>Ingestion</strong> — log posts with pasted copy and platform.{" "}
              <strong>Suggestion</strong> — browse what the team has indexed. Library, Feed import, and Org Settings
              are for marketing, trainers, and admins.
            </>
          )}
        </p>
        <div className="mt-3 flex flex-wrap gap-3 border-t border-gray-200/80 pt-3 dark:border-gray-600">
          <AdsHelpHint label="Marketing — tips">
            Use Library to define templates and policies. Optional AI assist can draft rows—you still confirm with
            Add.
          </AdsHelpHint>
          <AdsHelpHint label="Sales — tips">
            Paste caption text as it appears online; pick platform and time. Daily checklist progress uses ads saved
            for the calendar day.
          </AdsHelpHint>
        </div>
      </div>

      <details className="rounded-xl border border-gray-200 bg-white/80 px-3 py-2 shadow-sm dark:border-gray-700 dark:bg-zinc-900/80">
        <summary className="cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-100 [&::-webkit-details-marker]:hidden">
          Workspace details
        </summary>
        <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
          {workspaceOverrideActive ? (
            <p className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              Workspace org override is on. This panel uses the scoped organization for branding, indexed posts, and
              analyses.
            </p>
          ) : null}
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Brand</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">{brandName}</dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">App label</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">{appDisplayName}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-gray-500 dark:text-gray-400">Scoped organization id</dt>
              <dd className="font-mono text-xs text-gray-800 break-all dark:text-gray-200">
                {shortOrgId(scopedOrgId)}
              </dd>
            </div>
            {auth.profile?.email ? (
              <div className="sm:col-span-2">
                <dt className="text-gray-500 dark:text-gray-400">Signed in as</dt>
                <dd className="text-gray-800 dark:text-gray-200">{auth.profile.email}</dd>
              </div>
            ) : null}
          </dl>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Platform operators with a workspace org override use the same scoped id here as Knowledge Base and CRM
            sync.
          </p>
        </div>
      </details>
    </div>
  );
}
